import React, { useState, useEffect } from 'react';
import { Button } from '@common/Button';
import { RefreshCw, Download, Upload, Search, Filter, Info } from 'lucide-react';
import { Input, Select } from '@common/FormFields';
import { getHauteursLibres, importSampleData } from '@utils/wfs/importHauteursLibres';
import { logError, logInfo } from '@utils/logger';
import { Toast } from '@common/Toast';
import { HauteursLibresImport } from './HauteursLibresImport';
import { StructureDetailsModal } from './StructureDetailsModal';
import { COLLECTIONS } from '@utils/database/collections';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@config/firebase';

export const HauteursLibresManager = () => {
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [filteredStructures, setFilteredStructures] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [filters, setFilters] = useState({
    reference: '',
    municipalite: '',
    route: '',
    minHauteur: '',
    maxHauteur: ''
  });

  // Get unique municipalities and routes from the original structures list
  const uniqueMunicipalities = [...new Set(structures
    .map(s => s.municipalite)
    .filter(Boolean)
  )].sort();

  const routes = [...new Set(structures
    .map(s => s.route)
    .filter(Boolean)
  )].sort();

  // Load structures on mount
  useEffect(() => {
    loadStructures();
  }, []);

  // Apply filters when filters or structures change
  useEffect(() => {
    applyFilters();
  }, [filters, structures]);

  // Load structures from Firestore
  const loadStructures = async () => {
    setLoading(true);
    try {
      const data = await getHauteursLibres();
      setStructures(data);
      setFilteredStructures(data);
      logInfo(`Loaded ${data.length} structures`);
    } catch (error) {
      logError('Failed to load structures:', error);
      setError('Failed to load structures. Please try again.');
      setToast({
        type: 'error',
        message: 'Failed to load structures'
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...structures];
    
    // Apply reference filter
    if (filters.reference) {
      filtered = filtered.filter(structure => 
        structure.reference?.toLowerCase().includes(filters.reference.toLowerCase())
      );
    }
    
    // Apply municipality filter
    if (filters.municipalite && filters.municipalite !== 'All Municipalities') {
      filtered = filtered.filter(structure => 
        structure.municipalite === filters.municipalite
      );
    }
    
    // Apply route filter
    if (filters.route && filters.route !== 'All Routes') {
      filtered = filtered.filter(structure => 
        structure.route === filters.route
      );
    }
    
    // Apply min height filter
    if (filters.minHauteur) {
      const minHeight = parseFloat(filters.minHauteur);
      if (!isNaN(minHeight)) {
        filtered = filtered.filter(structure => 
          structure.hauteur !== null && structure.hauteur >= minHeight
        );
      }
    }
    
    // Apply max height filter
    if (filters.maxHauteur) {
      const maxHeight = parseFloat(filters.maxHauteur);
      if (!isNaN(maxHeight)) {
        filtered = filtered.filter(structure => 
          structure.hauteur !== null && structure.hauteur <= maxHeight
        );
      }
    }
    
    setFilteredStructures(filtered);
    logInfo(`Filtered to ${filtered.length} structures out of ${structures.length}`);
  };

  const handleStructureClick = (structure) => {
    setSelectedStructure(structure);
    setShowDetails(true);
  };

  const handleApplyFilters = () => {
    setLoading(true);
    applyFilters();
    setLoading(false);
  };

  const handleReset = () => {
    setFilters({
      reference: '',
      municipalite: '',
      route: '',
      minHauteur: '',
      maxHauteur: ''
    });
    setLoading(true);
    // Reset filters will trigger the useEffect
    setLoading(false);
  };

  const handleExportCSV = () => {
    try {
      // Create CSV content
      const headers = [
        'reference', 'route', 'sequence', 'municipalite', 'obstacle',
        'direction_SO', 'hauteur_SO', 'direction_NE', 'hauteur_NE',
        'hauteur', 'latitude', 'longitude'
      ];
      
      const csvContent = [
        headers.join(','),
        ...filteredStructures.map(s => headers.map(h => {
          if (h === 'latitude') return s.coordinates?.latitude || s.latitude || '';
          if (h === 'longitude') return s.coordinates?.longitude || s.longitude || '';
          return s[h] || '';
        }).join(','))
      ].join('\n');
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `hauteurs-libres-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setToast({
        type: 'success',
        message: `Exported ${filteredStructures.length} structures to CSV`
      });
      
      logInfo(`Exported ${filteredStructures.length} structures to CSV`);
    } catch (error) {
      logError('Failed to export to CSV:', error);
      setToast({
        type: 'error',
        message: 'Failed to export to CSV'
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-[#2A2B2E] p-6 rounded-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-medium text-white">Hauteurs Libres Manager</h2>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowImport(!showImport)}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Import CSV
            </Button>
            <Button
              onClick={loadStructures}
              className="bg-gray-700 hover:bg-gray-600 text-white"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Import Section */}
        {showImport && (
          <div className="mb-6">
            <HauteursLibresImport />
          </div>
        )}

        {/* Filters */}
        <div className="space-y-4 mb-6">
          <h3 className="text-white font-medium">Filters</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Input
              label="Reference"
              value={filters.reference}
              onChange={(e) => setFilters({ ...filters, reference: e.target.value })}
              placeholder="Search by reference"
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />
            
            <Select
              label="Municipality"
              value={filters.municipalite}
              onChange={(value) => setFilters({ ...filters, municipalite: value })}
              options={[
                { value: '', label: 'All Municipalities' },
                ...uniqueMunicipalities.map(m => ({ value: m, label: m }))
              ]}
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />
            
            <Select
              label="Route"
              value={filters.route}
              onChange={(value) => setFilters({ ...filters, route: value })}
              options={[
                { value: '', label: 'All Routes' },
                ...routes.map(r => ({ value: r, label: r }))
              ]}
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />
            
            <Input
              label="Min Height (m)"
              type="number"
              value={filters.minHauteur}
              onChange={(e) => setFilters({ ...filters, minHauteur: e.target.value })}
              placeholder="Minimum height"
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
              step="0.1"
            />
            
            <Input
              label="Max Height (m)"
              type="number"
              value={filters.maxHauteur}
              onChange={(e) => setFilters({ ...filters, maxHauteur: e.target.value })}
              placeholder="Maximum height"
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
              step="0.1"
            />
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={applyFilters}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Apply Filters
            </Button>
            
            <Button
              onClick={handleReset}
              className="bg-gray-700 hover:bg-gray-600 text-white"
            >
              Reset
            </Button>
            
            <Button
              onClick={handleExportCSV}
              className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2 ml-auto"
              disabled={filteredStructures.length === 0}
            >
              <Download className="w-4 h-4" />
              Export to CSV
            </Button>
          </div>
        </div>

        {/* Results Table */}
        <div className="bg-gray-700/50 rounded-lg overflow-hidden max-h-[calc(100vh-300px)]">
          <div className="p-4 border-b border-gray-600 flex justify-between items-center">
            <h3 className="text-white font-medium">
              Structures ({structures.length})
            </h3>
            {error && (
              <div className="text-red-400 text-sm">
                {error}
              </div>
            )}
          </div>
          <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-400px)]">
            <table className="w-full text-sm table-fixed">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="text-left py-2 px-4 text-gray-300 w-24">Reference</th>
                  <th className="text-left py-2 px-4 text-gray-300 w-64">Obstacle</th>
                  <th className="text-left py-2 px-4 text-gray-300 w-48">Municipality</th>
                  <th className="text-left py-2 px-4 text-gray-300 w-24">Route</th>
                  <th className="text-left py-2 px-4 text-gray-300 w-24">Height (m)</th>
                  <th className="text-left py-2 px-4 text-gray-300 w-48">Coordinates</th>
                </tr>
              </thead>
              <tbody>
                {filteredStructures.map((structure) => (
                  <tr 
                    key={structure.reference} 
                    className="border-t border-gray-700 hover:bg-gray-700/30 cursor-pointer"
                    onClick={() => handleStructureClick(structure)}
                  >
                    <td className="py-2 px-4 text-white whitespace-nowrap">{structure.reference}</td>
                    <td className="py-2 px-4 text-gray-300 truncate">{structure.obstacle}</td>
                    <td className="py-2 px-4 text-gray-300 truncate">{structure.municipalite}</td>
                    <td className="py-2 px-4 text-gray-300 whitespace-nowrap">{structure.route}</td>
                    <td className="py-2 px-4 text-gray-300 whitespace-nowrap">{structure.hauteur}</td>
                    <td className="py-2 px-4 text-gray-300 whitespace-nowrap">
                      {structure.coordinates ? 
                        `${structure.coordinates.latitude.toFixed(6)}, ${structure.coordinates.longitude.toFixed(6)}` : 
                        structure.latitude && structure.longitude ? 
                        `${structure.latitude.toFixed(6)}, ${structure.longitude.toFixed(6)}` : 
                        'No coordinates'
                      }
                    </td>
                  </tr>
                ))}
                {filteredStructures.length === 0 && (
                  <tr>
                    <td colSpan="6" className="py-4 text-center text-gray-400">
                      No structures found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default HauteursLibresManager;